/**
 * legalContent.js — SINGLE source of truth for every legal document on
 * SelleSend. Both the public marketing site and the members-area "Legal
 * Center" render from these exact objects, so nothing can drift.
 *
 * IMPORTANT: This file contains standard-industry legal language, drafted
 * as a working starting point. Nothing here is legal advice. Before final
 * publication, have your counsel review each document and swap in
 * jurisdiction-specific requirements (state privacy laws — CA, VA, CO, CT,
 * UT — GDPR/UK-GDPR if you serve EU customers, PCI-DSS references, etc).
 */
export const COMPANY = {
  name:      'SelleSend',
  legalName: 'SelleSend LLC',
  brand:     'SelleSend',
  address1:  '4395 Home Ave',
  address2:  'San Diego, CA 92105',
  phone:     '(619) 930-1074',
  supportEmail: 'support@sellesend.com',
  privacyEmail: 'privacy@sellesend.com',
  legalEmail:   'legal@sellesend.com',
  complianceEmail: 'compliance@sellesend.com',
  website:   'https://sellesend.com',
  founded:   '2024',
  jurisdiction: 'State of California, United States',
};

export const EFFECTIVE_DATE = 'February 17, 2026';

/* Los terminos de envio que Mike aprobo el 10-oct (SelleSend_FINAL.pdf,
   edicion del 29-set). El 15-oct pidio "Post our terms" y el 16-oct "I want
   these documents out": se publican como los mando, sin tocarlos. */
export const MASTER_SHIPPING_TERMS = {
  label: 'Master Shipping Terms & Shipper Instructions (PDF)',
  href: '/docs/SelleSend_Master_Shipping_Terms.pdf',
};

/* Mike, 15-oct y 16-oct: cada texto que decia que la proteccion de envios no
   tiene aseguradora pasa a decir esto. Su decision y su redaccion (solo se
   corrigio tipeo). Es el mismo texto que AVISO_COBERTURA en backend/config.py. */
export const COVERAGE_STATEMENT = "Shipping declared values are covered by Lloyd's. All warranties are a combination of limits, velocities and guarantors. See your member documents for full descriptions.";

/* El rol de SelleSend, con el texto que Mike mando el 16-oct (solo se
   corrigio tipeo). Es el mismo en la tarjeta publica, el documento
   regulatorio, los terminos, la tarjeta de /legal y el pie de la portada:
   se escribe una sola vez para que no se vuelvan a separar. */
export const ROLE_STATEMENT = [
  `${COMPANY.name} is not a bank, insurance company, common carrier, money transmitter, or shipping brand. We are a technology company that facilitates business flows and connectivity to service providers.`,
  `Card payments, ACH flows, debit transactions, eChecks, and money transfers are facilitated by third-party licensed banks and other financial services providers. We do not sit in the flow of funds except where conditions allow it under local and federal law. All international transactions are provided by licensed entities. All shipping services are direct, and client accounts are mandatory except where the carrier allows direct third-party access. If an alternative billing model is permissible, then a third party shall bill separately from ${COMPANY.name}. Where insurance applies and there is a covered peril (loss), the insurance shall provide a licensed adjuster, and ${COMPANY.name}'s responsibility shall not apply except to assist.`,
  // Mike, 16-oct 10:35, como continuacion de la declaracion. Solo tipeo corregido.
  `Since the shipping coverage is an insurance product and any other product that is determined as insured shall be approved or declined by the insurance company. In the event that the insurance determination is not favorable, it is understood this is not the responsibility of ${COMPANY.name}. All determinations are strictly the responsibility of the insurance and any such claim that ${COMPANY.name} is responsible shall be met with legal certainty.`,
];

/* --------------------------------------------------------------------- */
/* PUBLIC-FACING SUMMARIES                                                */
/* Short, plain-language versions that anyone (no login) can read.        */
/* These are marketing-quality digests, NOT the legally binding text.     */
/* Every summary is paired with the full member doc it summarises.        */
/* --------------------------------------------------------------------- */
export const PUBLIC_SUMMARIES = {
  'terms': {
    title: 'Terms of Service — Public Summary',
    tagline: 'The short version. Full binding contract is inside your account.',
    bullets: [
      `${COMPANY.name} is a shipping, protection, and commerce orchestration platform — not a bank, insurance carrier, or money transmitter.`,
      'You must be 18+ and provide accurate information.',
      'Fees for labels and protection are shown before you commit. Card top-ups have a 2.9% surcharge; ACH is free.',
      'You may not use us for illegal activity or to ship prohibited items.',
      'We warrant nothing beyond what applicable law requires. Liability is capped at $100 or 12 months of what you paid us, whichever is greater.',
      'Disputes go to binding arbitration in San Diego, California (JAMS).',
    ],
    memberLink: '/dashboard/legal/terms',
  },
  'privacy': {
    title: 'Privacy Policy — Public Summary',
    tagline: 'What we collect, why, and what we never do with it.',
    bullets: [
      `We collect what we need to ship your packages, verify your identity, prevent fraud, and process payments. That's it.`,
      'We do NOT sell your data to third-party data brokers. Ever.',
      'We do NOT use your item photos or messages to train public AI models.',
      'You have the right to know, correct, delete, and export your data (CCPA/CPRA, GDPR, VA/CO/CT/UT laws).',
      'All traffic is TLS 1.2+. Passwords are bcrypt-hashed. Card numbers are tokenized and never touch our servers.',
      'We honor Global Privacy Control (GPC) signals automatically.',
      'Full details, including exactly which vendors receive what, are inside your account.',
    ],
    memberLink: '/dashboard/legal/privacy',
  },
  'shipping-terms': {
    title: 'Shipping Terms — Public Summary',
    tagline: 'Rules that apply when we make you a label.',
    bullets: [
      'Rates are estimates based on your declared weight/dimensions. If the carrier re-measures higher, they will re-bill via a DIM adjustment.',
      'A label is a purchase, not a reservation. Unused labels can be refunded (up to 30 days) per carrier policy.',
      'Delivery estimates come from the carrier and are not guaranteed. Weather, customs, and network issues happen.',
      `You are responsible for the address you enter. Address-correction fees ($16+) are billed to your wallet.`,
      `${COVERAGE_STATEMENT} Claims: report within 48 hours of discovering a problem and send the supporting records within 14 days; a late report is reviewed, not denied for timing alone.`,
    ],
    memberLink: '/dashboard/legal/shipping-terms',
    fullDocument: MASTER_SHIPPING_TERMS,
  },
  'restricted-prohibited-products': {
    title: 'Restricted & Prohibited Products — Public Summary',
    tagline: 'What you cannot ship or list. Read before you list.',
    bullets: [
      'ABSOLUTELY prohibited: illegal drugs · firearms & ammunition · live animals · human remains · CSAM · counterfeits · stolen goods · ITAR-listed items · OFAC-embargoed destinations.',
      'RESTRICTED (approval + docs required): alcohol · tobacco/nicotine · Rx pharma · lithium-ion batteries · high-value collectibles >$5k · firearm accessories (FFL only).',
      'HAZMAT (aerosols, flammable liquids, corrosives, power banks) is shippable only after Hazmat mode is enabled on your account.',
      'International destinations may have additional restrictions — always check the destination country before shipping.',
      `Attempting to ship an absolutely-prohibited item results in immediate account suspension and, where applicable, referral to law enforcement.`,
      `Questions? Email ${COMPANY.complianceEmail} BEFORE you list or ship.`,
    ],
    memberLink: '/dashboard/legal/restricted-prohibited-products',
  },
  'aml-compliance': {
    title: 'AML & Compliance — Public Summary',
    tagline: 'How we prevent money-laundering and comply with U.S. financial law.',
    bullets: [
      'Every seller is verified against U.S. AML/KYC rules (Bank Secrecy Act, USA PATRIOT Act).',
      'Applicants are checked against a limited internal watchlist. We do not currently query the OFAC, UK HMT, EU or UN lists directly, and the check is not repeated after onboarding.',
      `${COMPANY.name} does not file Suspicious Activity Reports itself. Where a banking partner subject to those obligations is involved, activity may be reported to FinCEN through that partner.`,
      'AML records are retained for five (5) years after account closure, the period required by the Bank Secrecy Act. Broader account and shipping records are kept for seven (7) years, as stated in the Privacy Policy.',
      'Refusal to provide KYC information will result in account restriction.',
    ],
    memberLink: '/dashboard/legal/aml-compliance',
  },
  'regulatory': {
    title: 'Regulatory Disclosures — Public Summary',
    tagline: 'What SelleSend actually does, and what it does not do.',
    bullets: [
      ...ROLE_STATEMENT,
      'Certain industries are permitted only with additional documentation (firearms accessories, CBD, adult, high-ticket precious metals, tobacco, alcohol, licensed regulated industries).',
      `Enterprise underwriting/compliance letters: contact ${COMPANY.complianceEmail}.`,
    ],
    memberLink: '/dashboard/legal/regulatory',
  },
  'non-custodial': {
    title: 'Non-Custodial Disclosure — Public Summary',
    tagline: 'Where your wallet money actually lives.',
    bullets: [
      `Your ${COMPANY.name} wallet balance is an amount ${COMPANY.name} records as owed to you. It is not held in a separate account in your name.`,
      `${COMPANY.name} does not lend or invest customer balances.`,
      `There is no segregated account or trustee arrangement in place today.`,
      `Your balance is not FDIC insured through ${COMPANY.name}, and there is no pass-through insurance programme behind it today.`,
    ],
    memberLink: '/dashboard/legal/non-custodial',
  },
};


/* --------------------------------------------------------------------- */
/* 1. PRIVACY POLICY                                                     */
/* --------------------------------------------------------------------- */
export const PRIVACY_POLICY = {
  slug: 'privacy',
  title: 'Privacy Policy',
  intro: `${COMPANY.name} ("we," "us," "our") respects your privacy. This Privacy Policy explains what personal information we collect, how we use it, when we share it, how we secure it, and the rights you have over your data. It applies to visitors of ${COMPANY.website} and to registered members of the ${COMPANY.name} platform (the "Services").`,
  sections: [
    {
      heading: '1. Information we collect',
      body: [
        'Account data: name, email, phone number, business name, business address, EIN/Tax ID, and login credentials.',
        'Identity & KYC data: government-issued ID, selfie for liveness check, beneficial-owner information (collected to meet U.S. Bank Secrecy Act and FinCEN expectations that apply to this activity).',
        'Shipping data: sender and recipient names, street addresses, phone numbers, item descriptions, declared value, weight, and photographs of items you list.',
        'Financial data: bank account details (via Plaid or manual entry), debit/credit card details (via Stripe — we do not store raw card numbers on our servers), wallet balance history, and payout records.',
        'Device & usage data: IP address, browser type, device fingerprint, referrer URL, pages viewed, session duration, clicks, and error logs.',
        'Fraud-signal data: results returned by IPQualityScore, MaxMind minFraud, and Kount for the transactions you initiate.',
        'Communications: emails, SMS, in-app chat messages, and support tickets you send us.',
      ],
    },
    {
      heading: '2. How we use your information',
      body: [
        'To create and maintain your account.',
        'To verify your identity and your counterparty’s identity as required by U.S. AML/KYC law.',
        'To calculate shipping rates, generate carrier labels, and forward tracking events.',
        'To detect and prevent fraud, chargebacks, money-laundering, and prohibited-item shipping.',
        'To process payments, top up your wallet, disburse payouts, and reconcile disputes.',
        'To send you transactional notifications (label ready, shipment delivered, balance low, dispute opened). These are not marketing and cannot be unsubscribed.',
        'To send you marketing emails ONLY if you explicitly opt in. You can unsubscribe from these at any time.',
        'To improve the platform, debug errors, and train non-personalized quality-of-service models.',
        'To comply with lawful requests from courts, regulators, and law enforcement.',
      ],
    },
    {
      heading: '3. When we share your information',
      body: [
        `Carriers: USPS, UPS, FedEx, DHL, and our aggregator EasyPost receive the shipping data required to produce a label.`,
        'Payment processors: Stripe, and (where applicable) Adyen or NMI, process card and ACH payments on our behalf.',
        'Fraud vendors: IPQualityScore, MaxMind, and Kount receive limited attributes (email, IP, device signals) to return a risk score.',
        'Identity verification vendors receive your KYC submission solely to return a pass/fail decision.',
        'AI/ML processors: Emergent LLM (routing to OpenAI, Anthropic, and Google) receives item text and item photos when you request an AI grade. Prompts and outputs are not used to train third-party public models.',
        'Cloud infrastructure: Amazon Web Services, Google Cloud, MongoDB Atlas.',
        'Legal & compliance: courts, regulators, subpoena responders, and our auditors.',
        'Business transfers: in the event of merger, acquisition, or sale of substantially all assets, subject to the receiving party assuming this Policy.',
      ],
    },
    {
      heading: '4. What we do NOT do with your information',
      body: [
        'We do not sell your personal information to third-party data brokers.',
        'We do not use your item photos, item descriptions, or private messages to train public AI models.',
        'We do not disclose your buyer/seller counterparty identity to anyone outside the transaction, except as required by law.',
      ],
    },
    {
      heading: '5. Data retention',
      body: [
        'Account data is retained for the life of your account and for seven (7) years after closure, to satisfy IRS and carrier record-keeping rules. AML records are governed separately by the five (5) year period in our AML & Compliance Policy.',
        'Shipping and payment records are retained for seven (7) years.',
        'Support tickets are retained for two (2) years.',
        'Marketing preferences and unsubscribe records are retained indefinitely so we can honor your choice.',
      ],
    },
    {
      heading: '6. Your rights',
      body: [
        `California residents (CCPA/CPRA): you have the right to know, correct, delete, limit, and port your personal information, and to opt out of any "sale" or "sharing." We do not sell your data. To exercise your rights, email ${COMPANY.privacyEmail}.`,
        'Virginia, Colorado, Connecticut, Utah, Texas residents: you have the equivalent rights under your state privacy law.',
        `EU / UK residents: to the extent GDPR/UK-GDPR applies, you have the rights of access, rectification, erasure, restriction, portability, and objection. Contact ${COMPANY.privacyEmail}. Our data-controller is ${COMPANY.legalName}, ${COMPANY.address1}, ${COMPANY.address2}.`,
        'All users: you may close your account at any time from Settings → Privacy → Close Account. We will delete your personal data except records we are legally required to retain.',
      ],
    },
    {
      heading: '7. Security',
      body: [
        'All traffic to sellesend.com is encrypted in transit with TLS 1.2 or higher.',
        'Passwords are hashed with bcrypt (cost factor 12+). We never store plaintext passwords.',
        'Card numbers are tokenized by our processor and never touch our servers.',
        'We enforce role-based access, audit logging, and mandatory MFA on employee production access.',
      ],
    },
    {
      heading: '8. Cookies & tracking',
      body: [
        'We use strictly-necessary cookies for authentication, session persistence, and CSRF protection. These cannot be disabled without breaking the site.',
        'We use analytics cookies (self-hosted) to measure feature usage in aggregate. No cross-site tracking, no third-party ad-tech pixels.',
        'We honor Global Privacy Control (GPC) signals for California users.',
      ],
    },
    {
      heading: '9. Children',
      body: [
        `The Services are not directed to individuals under 18. We do not knowingly collect information from anyone under 18. If we learn we have, we delete it. Contact ${COMPANY.privacyEmail}.`,
      ],
    },
    {
      heading: '10. Changes to this Policy',
      body: [
        'We may update this Policy from time to time. Material changes will be announced by email to your registered address 30 days before they take effect. Continued use of the Services after the effective date constitutes acceptance.',
      ],
    },
    {
      heading: '11. Contact',
      body: [
        `Privacy questions: ${COMPANY.privacyEmail}`,
        `Legal notices: ${COMPANY.legalEmail}`,
        `Mail: ${COMPANY.legalName}, ${COMPANY.address1}, ${COMPANY.address2}`,
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* 2. TERMS OF SERVICE (MEMBER TERMS)                                    */
/* --------------------------------------------------------------------- */
export const TERMS_OF_SERVICE = {
  slug: 'terms',
  title: 'Terms of Service',
  intro: `These Terms of Service (the "Terms") govern your access to and use of the ${COMPANY.name} platform, including the website ${COMPANY.website}, our mobile experience, our APIs, and any related services (collectively, the "Services"). By creating an account, clicking "I agree," or otherwise using the Services, you agree to these Terms.`,
  sections: [
    {
      heading: '1. Who we are',
      body: [
        ...ROLE_STATEMENT,
      ],
    },
    {
      heading: '2. Eligibility',
      body: [
        'You must be at least 18 years old and legally able to enter into contracts.',
        'You must provide accurate and complete information during signup and keep it current.',
        'If you register on behalf of an entity, you represent that you have authority to bind that entity.',
        'We may refuse service, terminate accounts, or cancel transactions in our sole discretion if we believe eligibility rules are being violated.',
      ],
    },
    {
      heading: '3. Your account',
      body: [
        'You are responsible for keeping your login credentials confidential.',
        'You are responsible for all activity that occurs under your account.',
        'Notify us immediately at ' + COMPANY.supportEmail + ' if you suspect unauthorized access.',
        'You may not create multiple accounts to evade a suspension or to defraud counterparties.',
      ],
    },
    {
      heading: '4. Fees & payment',
      body: [
        'Shipping labels, protection premiums, subscription tiers, and add-on services are priced as displayed at the time of purchase. Prices may change; we will show the current price before you commit.',
        'Your wallet balance is your responsibility to maintain. Insufficient balance may cause label buys, protection claims, and payouts to be delayed.',
        'ACH top-ups are free. Card top-ups incur a 2.9% surcharge. Wire top-ups are subject to the sending bank’s fee.',
        'Chargebacks or reversals that cause a negative wallet balance must be resolved within seven (7) days or the account may be suspended.',
      ],
    },
    {
      heading: '5. Prohibited activities',
      body: [
        `You may not use the Services to conduct any activity that is illegal under U.S. federal law or the law of your jurisdiction, including but not limited to money laundering, terrorist financing, sanctions evasion, tax evasion, or fraud.`,
        `You may not ship, list, or facilitate the movement of restricted or prohibited items — see the ${COMPANY.name} Restricted & Prohibited Products policy, incorporated by reference.`,
        `You may not attempt to reverse-engineer, scrape, or overload our APIs; you may not disrupt other users; you may not upload malware.`,
        `You may not impersonate another person or business, or attempt to obtain another user’s credentials.`,
      ],
    },
    {
      heading: '6. Ownership & license',
      body: [
        `All ${COMPANY.name} software, trademarks, and content are our property or the property of our licensors. We grant you a limited, non-exclusive, revocable license to use the Services for their intended purpose.`,
        'Content you upload (photos, descriptions) remains yours. You grant us a non-exclusive, worldwide license to use it solely to operate the Services (e.g., to display your listing to buyers, produce shipping labels, or run an AI grade at your request).',
      ],
    },
    {
      heading: '7. Disclaimers',
      body: [
        'THE SERVICES ARE PROVIDED "AS-IS" AND "AS-AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EXPRESS OR IMPLIED, INCLUDING MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, AND NON-INFRINGEMENT.',
        `${COMPANY.name} does not underwrite insurance policies and does not guarantee that any specific carrier will accept, transport, or deliver a shipment. How wallet balances are actually held is described in the Non-Custodial Disclosure, and you should read it before keeping a balance on the platform.`,
      ],
    },
    {
      heading: '8. Limitation of liability',
      body: [
        `TO THE MAXIMUM EXTENT PERMITTED BY LAW, ${COMPANY.name.toUpperCase()}’S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR RELATING TO THE SERVICES IN A 12-MONTH PERIOD SHALL NOT EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID US DURING THAT PERIOD OR (B) ONE HUNDRED DOLLARS (US $100).`,
        'IN NO EVENT SHALL WE BE LIABLE FOR ANY INDIRECT, INCIDENTAL, CONSEQUENTIAL, SPECIAL, OR PUNITIVE DAMAGES, INCLUDING LOST PROFITS OR LOST DATA.',
      ],
    },
    {
      heading: '9. Indemnification',
      body: [
        `You agree to indemnify, defend, and hold ${COMPANY.name} and its officers, employees, and agents harmless from any claim, loss, liability, or expense (including reasonable attorneys’ fees) arising out of your use of the Services or your breach of these Terms.`,
      ],
    },
    {
      heading: '10. Dispute resolution & governing law',
      body: [
        `These Terms are governed by the laws of the ${COMPANY.jurisdiction}, without regard to conflict-of-laws rules.`,
        'Any dispute must first be raised via ' + COMPANY.legalEmail + '. If not resolved within thirty (30) days, the dispute shall be finally settled by binding arbitration in San Diego, California under the JAMS Comprehensive Arbitration Rules. Judgment on the award may be entered in any court of competent jurisdiction.',
        'You waive the right to participate in a class action or class arbitration.',
      ],
    },
    {
      heading: '11. Termination',
      body: [
        'You may close your account at any time from Settings → Close Account.',
        'We may suspend or terminate your account immediately if we reasonably believe you have violated these Terms, applicable law, or a partner’s policy.',
        'Upon termination, your access ends but your obligations under Sections 4, 7, 8, 9, and 10 survive.',
      ],
    },
    {
      heading: '12. Changes',
      body: [
        'We may amend these Terms from time to time. If a change is material, we will notify you by email at least 30 days before it takes effect. Continued use after the effective date constitutes acceptance.',
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* 3. RESTRICTED & PROHIBITED PRODUCTS                                    */
/* --------------------------------------------------------------------- */
export const RESTRICTED_PROHIBITED = {
  slug: 'restricted-prohibited-products',
  title: 'Restricted & Prohibited Products',
  intro: `Certain items may not be listed, shipped, or protected using ${COMPANY.name}. This list mirrors the requirements of our carrier and insurance partners (USPS, UPS, FedEx, DHL) and applicable U.S. federal law. When in doubt, contact ${COMPANY.complianceEmail} BEFORE listing or shipping.`,
  sections: [
    {
      heading: 'Absolutely prohibited (cannot be shipped or listed under any circumstance)',
      body: [
        'Illegal drugs and drug paraphernalia, including any cannabis product outside a state-legal license and any Schedule I controlled substance.',
        'Firearms, ammunition, explosives, and their component parts. This includes 80% receivers, silencer components, and 3D-printed weapon files.',
        'Live animals, human remains, human organs, blood, or biohazardous specimens.',
        'Child sexual abuse material (CSAM) or any other content that is illegal to possess.',
        'Counterfeit currency, counterfeit trademarks, or counterfeit government identification.',
        'Stolen goods, or any item you do not have legal title to sell.',
        'Items on the U.S. Munitions List (USML) or subject to ITAR without a valid export license.',
        'Items on U.S. OFAC sanctions lists, or destined for OFAC-embargoed countries.',
      ],
    },
    {
      heading: 'Restricted (allowed only with specific pre-approval and documentation)',
      body: [
        'Alcoholic beverages: allowed only for licensed sellers using ' + COMPANY.name + ' Alcohol Compliance mode, with age-verification at delivery.',
        'Tobacco and nicotine products: state-specific PACT-Act registration required.',
        'Prescription pharmaceuticals: closed-network only, DEA registration or state-licensed pharmacy required.',
        'Live plants and seeds: USDA phytosanitary certificate may be required.',
        'High-value collectibles (>$5,000 declared value): must use SelleSend Premium Protection tier and photo/video authentication.',
        'Lithium-ion batteries and devices containing them: must ship in accordance with IATA / 49 CFR Hazmat rules. Watt-hour rating disclosure required.',
        'Firearm parts other than the receiver (grips, scopes, magazines): allowed only for FFL-verified sellers.',
      ],
    },
    {
      heading: 'Hazardous materials (Hazmat)',
      body: [
        'Aerosols, flammable liquids, corrosives, poisons, and radioactive materials are shippable only under carrier Hazmat contracts. Contact ' + COMPANY.complianceEmail + ' to enable Hazmat mode on your account.',
        'Common consumer Hazmat items include nail polish remover, perfume, spray paint, hand sanitizer, and lithium-ion power banks.',
      ],
    },
    {
      heading: 'International shipping restrictions',
      body: [
        'Some items legal to ship domestically are prohibited to specific destinations. Always check the destination country’s prohibited-item list on the carrier’s website before shipping internationally.',
        'False or under-declared customs values will result in shipment cancellation and account review.',
      ],
    },
    {
      heading: 'Enforcement',
      body: [
        'Attempts to list, ship, or protect any absolutely-prohibited item will result in immediate account suspension, forfeiture of pending payouts (subject to lawful dispute), reporting to carriers, and, where applicable, referral to law enforcement.',
        'Discrepancy scan and item-verification checks are performed on every shipment. Items that do not match their declared description may be rejected at label print, at pickup, or in transit — with the seller responsible for retrieval fees.',
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* 4. AML & COMPLIANCE                                                    */
/* --------------------------------------------------------------------- */
export const AML_COMPLIANCE = {
  slug: 'aml-compliance',
  title: 'Anti-Money-Laundering (AML) & Compliance Policy',
  intro: `${COMPANY.name} works with regulated payment processors and, where applicable, FinCEN-registered banking partners. This policy describes the controls ${COMPANY.name} actually operates today, and says plainly where a control is not yet in place. This policy explains what we do to prevent money-laundering, terrorist financing, and sanctions evasion, and what we require from you.`,
  sections: [
    {
      heading: 'Customer identification (KYC)',
      body: [
        'Individual sellers must provide full legal name, date of birth, permanent residential address, and government-issued photo ID.',
        'Business sellers must provide the legal entity name, EIN, registered address, and beneficial-owner information for every individual owning ≥25% of the entity.',
        'Non-U.S. persons must provide a passport and, where applicable, a completed IRS Form W-8BEN or W-8BEN-E.',
      ],
    },
    {
      heading: 'Sanctions screening',
      body: [
        `Applicants are checked against a limited internal watchlist at the time of application. ${COMPANY.name} does not currently query the OFAC, UK HMT, EU or UN sanctions lists directly, and screening is not repeated after onboarding. Comprehensive screening against those lists is being implemented; this page will be updated when it is, and not before.`,
        `This means you should not treat an approved application as confirmation that a counterparty is not sanctioned. If ${COMPANY.name} becomes aware that you or a counterparty is sanctioned, the account is frozen and the freeze is reported to OFAC.`,
        `Certain destination countries are blocked outright at the point of shipment.`,
      ],
    },
    {
      heading: 'Transaction monitoring',
      body: [
        `${COMPANY.name} does not hold customer funds, so it does not see the money flows that structuring and layering describe. What it does monitor is shipment activity: repeated deliveries to the same address from different buyers, mismatched or high-risk destinations, restricted commodities, and unusual wallet top-up patterns.`,
        `Where ${COMPANY.name} has a banking partner subject to Suspicious Activity Report obligations, suspicious activity may be reported to FinCEN through that partner's process, and by law we may not disclose to you that a report has been filed. ${COMPANY.name} does not file Suspicious Activity Reports itself.`,
      ],
    },
    {
      heading: 'Recordkeeping',
      body: [
        'We retain customer identification, transaction records, and audit logs for five (5) years after account closure, which is the period the Bank Secrecy Act requires for these records.',
        'Our Privacy Policy states seven (7) years for account and shipping records. The two periods are different on purpose: five years is the BSA floor for the AML records named above, and seven years covers the broader account and tax records described there. Neither replaces the other.',
      ],
    },
    {
      heading: 'Your obligations',
      body: [
        'Provide truthful KYC information and update it promptly if it changes.',
        'Do not use the Services on behalf of a third party without disclosing that fact and completing beneficial-owner disclosure.',
        'Cooperate with reasonable requests for information from ' + COMPANY.name + ', or from a payment or banking partner where one is involved. Refusal will result in account restriction.',
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* 5. SHIPPING TERMS                                                      */
/* --------------------------------------------------------------------- */
export const SHIPPING_TERMS = {
  slug: 'shipping-terms',
  title: 'Shipping Terms',
  fullDocument: MASTER_SHIPPING_TERMS,
  intro: `These Shipping Terms describe the rules that apply when you use ${COMPANY.name} to produce a shipping label, arrange pickup, or track a shipment. They supplement — but do not replace — the terms and conditions of the carrier who ultimately handles your package (USPS, UPS, FedEx, DHL, or other).`,
  sections: [
    {
      heading: '1. Rate quotes',
      body: [
        'All rates displayed are estimates based on the sender-declared weight and dimensions and the carrier’s published rate card at the time of quote.',
        'If the actual package weight or dimensions differ from the declared values, the carrier will re-bill via a Dimensional Weight Adjustment. That adjustment appears on your wallet ledger and is not disputable by ' + COMPANY.name + '.',
      ],
    },
    {
      heading: '2. Label purchase',
      body: [
        'A label is a purchase, not a reservation. Once purchased, your wallet is charged the sell-rate + shipper label fee + fuel surcharge + selected add-ons.',
        'Unused labels can be refunded within thirty (30) days per the carrier’s refund policy. Some carriers do not refund; you will see the refund policy before purchase.',
      ],
    },
    {
      heading: '3. Pickup & drop-off',
      body: [
        'Pickup is scheduled at your request. Missed pickups because the package was not ready are not refundable.',
        'For drop-off, deposit the package at any authorized carrier location. Damage or loss before the package enters the carrier’s scan system is not our responsibility.',
      ],
    },
    {
      heading: '4. Protection & insurance',
      body: [
        `${COVERAGE_STATEMENT} Coverage limits, exclusions, deductibles, and claim procedures are set out in the Protection certificate issued at purchase.`,
        `There is no automated process that pays a claim out. The payable amount is calculated and the claim is recorded; payment is a decision ${COMPANY.name} makes and executes by hand.`,
        'Report a problem with a protected shipment within forty-eight (48) hours of discovering it and send the supporting records within fourteen (14) days, the same targets printed on your certificate. They are targets, not deadlines: a late report is reviewed, not denied for timing alone. Photos of packaging and content are required.',
      ],
    },
    {
      heading: '5. Restricted & prohibited items',
      body: [
        'See the ' + COMPANY.name + ' Restricted & Prohibited Products policy. Shipping a prohibited item voids Protection and can result in carrier fines that are your responsibility.',
      ],
    },
    {
      heading: '6. Delivery times',
      body: [
        'Delivery estimates are provided by the carrier and are not guaranteed. ' + COMPANY.name + ' is not liable for delays caused by weather, carrier network issues, customs, or force majeure.',
      ],
    },
    {
      heading: '7. Address accuracy',
      body: [
        'You are responsible for the accuracy of the recipient’s address. Address-corrections applied by the carrier incur a $16.00-and-up fee, billed to your wallet.',
        `Where available, ${COMPANY.name} verifies addresses with USPS Address Validation and Google Places. This is a best-effort feature and does not shift liability.`,
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* 6. REGULATORY DISCLOSURES                                              */
/* --------------------------------------------------------------------- */
export const REGULATORY = {
  slug: 'regulatory',
  title: 'Regulatory Disclosures',
  intro: `${COMPANY.name} is an orchestration platform that connects licensed partners with sellers, buyers, and shippers. This page discloses our role, our partners, and how customer funds are handled.`,
  sections: [
    {
      heading: `Role of ${COMPANY.name}`,
      body: [
        ...ROLE_STATEMENT,
      ],
    },
    {
      heading: 'Enhanced-review industries',
      body: [
        'Certain industries are permitted only with additional documentation and quarterly review, because of higher AML or chargeback risk. Enhanced-review categories include: firearms accessories, adult products, CBD and other hemp derivatives, high-ticket precious metals, cryptocurrency mining hardware, tobacco/nicotine, licensed alcohol, live-training courses with recurring billing, and any activity requiring a state-specific license.',
        `To operate in an enhanced-review industry, request enrollment at ${COMPANY.complianceEmail}. We will provide the documentation checklist and quarterly reporting cadence.`,
      ],
    },
    {
      heading: 'Prohibited activities',
      body: [
        `${COMPANY.name} will not knowingly serve any customer engaged in: unlicensed money transmission, unlicensed money-services business activity, unlicensed sale of insurance, unlicensed practice of law, human trafficking, forced labor, or any activity that is illegal at the U.S. federal level. Refer to the Restricted & Prohibited Products policy for the shipping-side list.`,
      ],
    },
    {
      heading: 'Underwriting & compliance review',
      body: [
        `If you are a payment processor, financial institution, or high-volume merchant, we may require additional underwriting documentation (financial statements, principal background check, business licenses, industry-specific certifications). Contact ${COMPANY.complianceEmail}.`,
      ],
    },
    {
      heading: 'Data & privacy',
      body: [
        `See the ${COMPANY.name} Privacy Policy for a full description of how we collect, use, and share personal information.`,
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* 7. NON-CUSTODIAL DISCLOSURE (standalone one-pager)                     */
/* --------------------------------------------------------------------- */
export const NON_CUSTODIAL = {
  slug: 'non-custodial',
  title: 'Non-Custodial Disclosure',
  intro: `This page explains where the money in your wallet actually sits. It was rewritten in September 2026 because the previous version described an arrangement that is not in place.`,
  sections: [
    {
      heading: 'Where your money actually lives',
      body: [
        `When you top up your ${COMPANY.name} wallet, the payment is processed by Stripe and settles into the ${COMPANY.name} account with that processor. Your wallet balance is a record ${COMPANY.name} keeps of what it owes you against that top-up.`,
        `There is no separate For-Benefit-Of account, no third-party custodian, and no trustee arrangement in place today. We are describing this plainly because an earlier version of this page said otherwise.`,
        `Funds leave that balance when you commit to a purchase: a label, protection, or a payout.`,
      ],
    },
    {
      heading: 'FDIC insurance',
      body: [
        `Your wallet balance is not FDIC insured through ${COMPANY.name}, and there is no pass-through insurance programme behind it today.`,
      ],
    },
    {
      heading: 'What this means for you',
      body: [
        `There is no segregated account and no independent trustee arrangement today, so you should not assume your balance is protected from ${COMPANY.name}'s own obligations. What happens to a balance if ${COMPANY.name} ceased operations is a question for your counsel, not something this page should answer for you.`,
        `${COMPANY.name} does not lend or invest customer balances. Revenue comes only from platform fees, markups, and value-added services.`,
        `Keeping a large balance sitting in the wallet is therefore not the same as keeping it in a bank. Top up what you expect to spend.`,
      ],
    },
  ],
};

/* --------------------------------------------------------------------- */
/* Export catalogue used by the pages/nav                                */
/* --------------------------------------------------------------------- */
export const LEGAL_DOCS = [
  TERMS_OF_SERVICE,
  PRIVACY_POLICY,
  SHIPPING_TERMS,
  RESTRICTED_PROHIBITED,
  AML_COMPLIANCE,
  REGULATORY,
  NON_CUSTODIAL,
];

export const LEGAL_BY_SLUG = Object.fromEntries(LEGAL_DOCS.map((d) => [d.slug, d]));
